/**
 * The two private share artefacts (M17, PRD §1.8 loops 1 and 3) — the standing
 * flex and the receipt. Both are minted per knock, because both are authorised
 * by a signed token the server holds: nothing is shareable until it says so.
 *
 * The affordance is one quiet line until it is used. A refusal — most often
 * `STANDING_NOT_SHAREABLE`, which is simply what most knocks are — removes the
 * affordance entirely and says nothing: teasing somebody with a card they cannot
 * have would be the opposite of dry. Nothing renders half-loaded either, so the
 * row arrives in one piece and the screen below it never jumps.
 */
import { useCallback, useState } from "react";
import { createShareLink, type ShareKind, type ShareLink } from "../../lib/api";
import { track } from "../../lib/telemetry";
import { LinkButton } from "../ui/button";
import { ShareRow } from "./share-row";

export const STANDING_SHARE_LABEL = "Share this";
export const RECEIPT_SHARE_LABEL = "Share the receipt";

type Props = {
  kind: ShareKind;
  knockId: string;
  /** The affordance's own words — "Share this", "Share the receipt". */
  label: string;
  /** The suggested post, derived from what this knock actually produced. */
  postText: string;
  imageAlt: string;
};

export function KnockShare({ kind, knockId, label, postText, imageAlt }: Props) {
  const [link, setLink] = useState<ShareLink | null>(null);
  const [busy, setBusy] = useState(false);
  /** The server declined to mint. There is nothing honest to show, so nothing is. */
  const [withheld, setWithheld] = useState(false);

  const mint = useCallback(() => {
    if (busy) return;
    setBusy(true);
    void (async () => {
      const r = await createShareLink(kind, knockId);
      setBusy(false);
      if (!r.ok) {
        setWithheld(true);
        return;
      }
      track({ name: "share_card_generated", kind, posted: false });
      setLink(r.link);
    })();
  }, [busy, kind, knockId]);

  if (withheld) return null;

  if (link) {
    return (
      <ShareRow
        imageUrl={link.og_image_url}
        imageAlt={imageAlt}
        shareUrl={link.share_url}
        postText={postText}
        kind={kind}
      />
    );
  }

  return (
    <p className="mt-6">
      <LinkButton type="button" onClick={mint} disabled={busy}>
        {label}
      </LinkButton>
    </p>
  );
}
